// LEGACY: Uses SCSS with CSS modules. New code should use @metarouter-private/mr-mui components instead.
// LEGACY: Uses @ion/components. New code should use @metarouter-private/mr-mui components instead.
/* eslint-disable filename-export/match-default-export */
/* disabling rule until refactor of analytics builder */

import React from 'react';
import pt from 'prop-types';
import { MrH, MrInputText } from '@ion/components';
import s from './index.module.scss';
import AnalyticsBuilderFileHosting, { HOSTING_TYPES } from './analytics-builder-file-hosting';
import AnalyticsBuilderConsent from './analytics-builder-consent';
import AnalyticsBuilderConsentSource from './AnalyticsBuilderConsentSource';
import AnalyticsBuilderAdvancedSettingsAccordion from './analytics-builder-advanced-settings-accordion';
import { ConsentCategoryTable } from 'app/components/analytics-builder/identity-sync-card/components';
import FeatureGate from 'app/launch-darkly/gates/feature-gate';
import { FEATURE_FLAGS } from 'app/launch-darkly/featureFlags';

export const CDN_HOSTING_LABEL = 'File Hosting URL';
const ANALYTICS_JS_HOSTING_LABEL = fileNameText => `${fileNameText} Hosting URL`;
const CDN_HOSTING_HELPER_TEXT = fileNameText =>
  `The file hosting URL where your ${fileNameText} file will be cached after fetching from the MetaRouter Cluster.`;
const ANALYTICS_JS_HOSTING_HELPER_TEXT = fileNameText => `Where the ${fileNameText} file will be located`;
export const CONSENT_COOKIE_NAME = 'OptanonConsent';

const AnalyticsBuilderSettings = ({
  formState,
  setField,
  validateField,
  validateRequired,
  validateUrl,
  validateIngestionUrl,
  resetError,
  fileNameText,
  isStandAloneSync,
  isUnminifyFileEnabled,
  isBotDetectionEnabled,
}) => {
  const isMRHosted = formState.fileHostingType?.value === HOSTING_TYPES.metaRouter;

  const validURLMsg = `Value must be a URL and cannot end with "/"`;

  const legacyComplianceFields = (
    <>
      <MrInputText
        className={s.textField}
        label="Cookie Name"
        onChange={setField}
        value={formState.cookieName?.value ?? ''}
        onBlur={validateField}
        onInput={resetError}
        name="cookieName"
        errorMsg={formState.cookieName?.errorMsg}
        validators={[validateRequired()]}
        helperText={`Add the cookie name that your consent management solution uses to store user consent preferences. For example, if using OneTrust, this would be ${CONSENT_COOKIE_NAME}.`}
        placeholder={CONSENT_COOKIE_NAME}
        fullWidth
        required
      />
      <FeatureGate flagName={FEATURE_FLAGS.configurableConsentMvp}>
        <FeatureGate flagName={FEATURE_FLAGS.matchIdConsent}>
          <ConsentCategoryTable
            resetError={resetError}
            formState={formState}
            setField={setField}
            syncName="matchIDConsentCode"
            validateField={undefined}
            validateRequired={undefined}
            title="anonymousId Consent Type Code"
            prefix="consent"
            addEmptyValue={true}
            helperText={`If a category is selected and it matches the consent value in ${formState.cookieName?.value}, the ajs_anonymous_id cookie will be deleted on opt-out.`}
          />
        </FeatureGate>
      </FeatureGate>
    </>
  );

  return (
    <div>
      <section className={s.section}>
        <MrH h="h5" sans styleNames="noMargin">
          <div className={s.circle}>1</div> File location and settings
        </MrH>
        <div className={s.subSection}>
          <AnalyticsBuilderFileHosting
            value={formState?.fileHostingType?.value}
            onChange={setField}
            errorMsg={formState.fileHostingType?.errorMsg}
            resetError={resetError}
            fileNameText={fileNameText}
            required
          />
          <MrInputText
            label={isMRHosted ? CDN_HOSTING_LABEL : ANALYTICS_JS_HOSTING_LABEL(fileNameText)}
            onChange={setField}
            value={formState.cdnURL?.value ?? ''}
            onBlur={validateField}
            onInput={resetError}
            name="cdnURL"
            errorMsg={formState.cdnURL?.errorMsg}
            helperText={
              isMRHosted ? CDN_HOSTING_HELPER_TEXT(fileNameText) : ANALYTICS_JS_HOSTING_HELPER_TEXT(fileNameText)
            }
            validators={[validateRequired(), validateUrl(validURLMsg, false)]}
            placeholder={isMRHosted ? 'cdn.metarouter.io' : 'yourdomain.com/scripts'}
            prefix="https://"
            fullWidth
            required
          />
          {/* Standalone builds, snippets and uploads don't need the ingestion host */}
          {!isStandAloneSync && (
            <MrInputText
              label="Ingestion Website URL"
              onChange={setField}
              value={formState.host?.value ?? ''}
              onBlur={validateField}
              onInput={resetError}
              name="host"
              errorMsg={formState.host?.errorMsg}
              validators={[validateRequired(), validateUrl(validURLMsg, false), validateIngestionUrl()]}
              helperText="The event ingestion endpoint for your MetaRouter Cluster."
              placeholder="yourdomain.com"
              prefix="https://"
              fullWidth
              required
            />
          )}
        </div>
      </section>
      <section className={s.section} style={{ paddingTop: 20, paddingBottom: 20 }}>
        <AnalyticsBuilderAdvancedSettingsAccordion
          formState={formState}
          setField={setField}
          validateField={validateField}
          resetError={resetError}
          isStandAloneSync={isStandAloneSync}
          isUnminifyFileEnabled={isUnminifyFileEnabled}
          isBotDetectionEnabled={isBotDetectionEnabled}
        />
      </section>
      <section className={s.section}>
        <MrH h="h5" sans styleNames="noMargin">
          <div className={s.circle}>2</div>
          Compliance Settings
        </MrH>
        <div className={s.subSection}>
          <AnalyticsBuilderConsent
            value={formState.impliedConsent?.value}
            onChange={setField}
            resetError={resetError}
            errorMsg={formState.impliedConsent?.errorMsg}
          />

          <FeatureGate flagName={FEATURE_FLAGS.ecmConfigurableConsent} fallback={legacyComplianceFields}>
            <AnalyticsBuilderConsentSource
              formState={formState}
              setField={setField}
              validateField={validateField}
              validateRequired={validateRequired}
              resetError={resetError}
            />
          </FeatureGate>
        </div>
      </section>
    </div>
  );
};

AnalyticsBuilderSettings.propTypes = {
  writekey: pt.string,
  formState: pt.object,
  setField: pt.func,
  validateField: pt.func,
  validateRequired: pt.func,
  validateUrl: pt.func,
  validateIngestionUrl: pt.func,
  resetError: pt.func,
  fileNameText: pt.string,
  isStandAloneSync: pt.bool,
  isUnminifyFileEnabled: pt.bool,
  isBotDetectionEnabled: pt.bool,
};

export default AnalyticsBuilderSettings;
